import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  MessageSquare,
  TrendingUp,
  Users,
  Package,
  FileText,
  Settings,
  Mail,
  Calendar,
  MoreHorizontal,
  X,
  LogOut,
  ReceiptText,
  Gavel,
  Pin,
  LayoutDashboard,
  IdCard,
  Truck,
  ListTodo,
  FilePlus,
} from 'lucide-react';
import logoWordmark from '../assets/logo-watermark.png';
import { BrandIcon } from './BrandLogo';
import { supabase } from '../lib/supabaseClient';
import { IMPRESSUM_URL } from '../lib/legalLinks';
import { resetPostHogAfterLogout } from '../lib/posthogClient';
import { resetSentryAfterLogout } from '../lib/sentryClient';
import { fetchMe, getCachedCapabilities, invalidateMeCache } from '../lib/meClient';
import {
  clearSidebarPinnedSnapshot,
  fetchSidebarPinnedPreference,
  getSidebarPinnedSnapshot,
  hasSidebarPinnedCache,
  setSidebarPinnedSnapshot,
} from '../lib/sidebarPinnedPreference';
import { useSyncActiveInstanceFromUrl } from '../hooks/useSyncActiveInstanceFromUrl';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { resolveWorkspaceProfile } from '../lib/workspaceProfile';
import { buildNavItems, type NavItemKey } from './navigation/navItems';

type AppSidebarProps = {
  active?: 'dashboard' | 'chat' | 'leads' | 'invoices' | 'inbox' | 'calendar' | 'contacts' | 'settings' | 'stammdaten' | 'templates' | 'employees' | 'ausschreibungen' | 'fuhrpark' | 'aufgaben';
};

const NAV_ITEM_ACTIVE: Record<NavItemKey, AppSidebarProps['active'] | null> = {
  dashboard: 'dashboard',
  new_offer: null,
  chat: 'chat',
  inbox: 'inbox',
  calendar: 'calendar',
  aufgaben: 'aufgaben',
  leads: 'leads',
  invoices: 'invoices',
  contacts: 'contacts',
  stammdaten: 'stammdaten',
  ausschreibungen: 'ausschreibungen',
  templates: 'templates',
  employees: 'employees',
  fuhrpark: 'fuhrpark',
};

function navItemIcon(key: NavItemKey): React.ReactNode {
  const cls = 'w-5 h-5';
  switch (key) {
    case 'dashboard': return <LayoutDashboard className={cls} />;
    case 'new_offer': return <FilePlus className={cls} />;
    case 'chat': return <MessageSquare className={cls} />;
    case 'inbox': return <Mail className={cls} />;
    case 'calendar': return <Calendar className={cls} />;
    case 'aufgaben': return <ListTodo className={cls} />;
    case 'leads': return <TrendingUp className={cls} />;
    case 'invoices': return <ReceiptText className={cls} />;
    case 'contacts': return <Users className={cls} />;
    case 'stammdaten': return <Package className={cls} />;
    case 'ausschreibungen': return <Gavel className={cls} />;
    case 'templates': return <FileText className={cls} />;
    case 'employees': return <IdCard className={cls} />;
    case 'fuhrpark': return <Truck className={cls} />;
  }
}

export const AppSidebar: React.FC<AppSidebarProps> = ({ active }) => {
  // Mounted on all app shells including LeadDetail (no TopBar) — keep URL/session instance in sync here only.
  useSyncActiveInstanceFromUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const plainPath = React.useMemo(() => path.replace(/^\/i\/[^/]+/, ''), [path]);
  const instancePrefix = React.useMemo(() => {
    const match = path.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [path]);
  const inInstancePath = React.useCallback((suffix: string) => `${instancePrefix}${suffix}`, [instancePrefix]);

  /** /stammdaten darf nicht in 'leads' fallen — sonst leuchten Übersicht + Projekte fälschlich */
  const effectiveActive: AppSidebarProps['active'] =
    active ??
    (plainPath.startsWith('/stammdaten')
      ? 'stammdaten'
      : plainPath.startsWith('/dashboard')
        ? 'dashboard'
      : plainPath.startsWith('/chat')
        ? 'chat'
      : plainPath.startsWith('/templates')
        ? 'templates'
      : plainPath.startsWith('/mitarbeiter')
        ? 'employees'
      : plainPath.startsWith('/ausschreibungen')
        ? 'ausschreibungen'
      : plainPath.startsWith('/fleet')
        ? 'fuhrpark'
      : plainPath.startsWith('/todos') || plainPath.startsWith('/aufgaben')
        ? 'aufgaben'
      : plainPath.startsWith('/invoices') || plainPath.startsWith('/rechnungen')
        ? 'invoices'
      : plainPath.startsWith('/inbox')
        ? 'inbox'
        : plainPath.startsWith('/calendar')
          ? 'calendar'
          : plainPath.startsWith('/contacts') || plainPath.startsWith('/organizations')
            ? 'contacts'
            : plainPath.startsWith('/settings')
              ? 'settings'
              : plainPath.startsWith('/projects') || plainPath.startsWith('/leads')
                ? 'leads'
                : 'leads');

  const navItemClass = (isActive: boolean) =>
    `sidebar-item ${isActive ? 'active' : ''}`;

  // ── Mobile drawer state ──
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [capabilities, setCapabilities] = React.useState<Record<string, boolean> | null>(() => getCachedCapabilities());

  const [sidebarPinned, setSidebarPinned] = React.useState(getSidebarPinnedSnapshot);
  const [sidebarPrefLoaded, setSidebarPrefLoaded] = React.useState(hasSidebarPinnedCache);
  const sidebarPrefSavingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    fetchSidebarPinnedPreference()
      .then(() => {
        if (cancelled) return;
        setSidebarPinned(getSidebarPinnedSnapshot());
      })
      .finally(() => {
        if (!cancelled) setSidebarPrefLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled) return;
      setCapabilities((me?.capabilities as Record<string, boolean> | undefined) ?? {});
    });
    return () => { cancelled = true; };
  }, []);

  const canView = React.useCallback((key?: string) => {
    if (!key) return true;
    // Until capabilities are known, hide gated items to avoid a full-nav flash.
    if (!capabilities) return false;
    return capabilities[key] === true;
  }, [capabilities]);

  // Einträge und Reihenfolge je Startprofil (Angebots-Paket: Angebots-Workflow zuerst).
  const { modules: instanceModules } = useModuleAccess();
  const navItems = React.useMemo(
    () => buildNavItems(resolveWorkspaceProfile(instanceModules), canView),
    [instanceModules, canView],
  );
  const isNavItemActive = (key: NavItemKey) => key !== 'new_offer' && effectiveActive === NAV_ITEM_ACTIVE[key];

  const settingsHomePath = capabilities && capabilities['web.einstellungen.view'] === true
    ? inInstancePath('/settings/business')
    : inInstancePath('/settings/personal');

  React.useEffect(() => {
    const root = document.documentElement;
    if (sidebarPinned) {
      root.setAttribute('data-app-nav-pinned', '');
      root.style.setProperty('--app-nav-width', '13rem');
    } else {
      root.removeAttribute('data-app-nav-pinned');
      root.style.setProperty('--app-nav-width', '3.5rem');
    }
    return () => {
      root.removeAttribute('data-app-nav-pinned');
      root.style.removeProperty('--app-nav-width');
    };
  }, [sidebarPinned]);

  const toggleSidebarPinned = React.useCallback(() => {
    if (sidebarPrefSavingRef.current) return;
    const next = !sidebarPinned;
    setSidebarPinnedSnapshot(next, { userInitiated: true });
    setSidebarPinned(next);
    sidebarPrefSavingRef.current = true;
    fetch('/api/me/preferences/ui', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sidebarPinned: next }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok }) => {
        if (!ok) {
          setSidebarPinnedSnapshot(!next);
          setSidebarPinned(!next);
        }
      })
      .catch(() => {
        setSidebarPinnedSnapshot(!next);
        setSidebarPinned(!next);
      })
      .finally(() => { sidebarPrefSavingRef.current = false; });
  }, [sidebarPinned]);

  const sidebarAsideClass = [
    'app-main-nav-sidebar hidden md:flex bg-[#1b2531] flex-col pb-4 z-20 transition-all duration-300 ease-in-out group overflow-hidden shrink-0 h-full min-h-0 sticky top-0 self-stretch',
    sidebarPinned ? 'w-52 sidebar-pinned' : 'w-14 hover:w-52',
  ].join(' ');

  const pinButtonClass = [
    'shrink-0 w-8 h-8 flex items-center justify-center rounded-md transition-colors',
    sidebarPinned
      ? 'flex text-pipedrive-blue hover:bg-white/10'
      : 'hidden group-hover:flex text-slate-400 hover:text-white hover:bg-white/10',
  ].join(' ');

  // Close drawer on route change
  React.useEffect(() => { setDrawerOpen(false); }, [path]);

  // Prevent body scroll when drawer is open
  React.useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const handleLogout = async () => {
    setDrawerOpen(false);
    try {
      await Promise.all([
        fetch('/api/auth/logout', { method: 'POST' }),
        supabase.auth.signOut(),
      ]);
    } catch { /* noop */ }
    clearSidebarPinnedSnapshot();
    invalidateMeCache();
    resetPostHogAfterLogout();
    resetSentryAfterLogout();
    navigate('/login', { replace: true });
  };

  const drawerNavItem = (
    to: string,
    icon: React.ReactNode,
    label: string,
    isActive: boolean,
    disabled = false,
  ) => {
    if (disabled) {
      return (
        <span
          key={label}
          className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-app-text-subtle opacity-40 cursor-not-allowed select-none"
        >
          <span className="w-6 flex items-center justify-center shrink-0">{icon}</span>
          <span className="text-base font-medium">{label}</span>
        </span>
      );
    }
    return (
      <Link
        key={label}
        to={to}
        onClick={() => setDrawerOpen(false)}
        className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors ${
          isActive
            ? 'bg-pipedrive-blue text-white'
            : 'text-slate-200 hover:bg-white/10 active:bg-white/20'
        }`}
      >
        <span className="w-6 flex items-center justify-center shrink-0">{icon}</span>
        <span className="text-base font-medium">{label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* ── Desktop sidebar (hidden on mobile) ── */}
      <aside className={sidebarAsideClass}>
        <div className="flex h-14 shrink-0 items-center px-2">
          <Link
            to={inInstancePath('/dashboard')}
            className="flex items-center w-full h-10 px-1 rounded-md hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:outline-none transition-colors"
            aria-label="Zur Übersicht"
          >
            <div className="w-10 h-10 flex items-center justify-center shrink-0">
              <BrandIcon size={24} onDark />
            </div>
            <span
              className={`sidebar-label !opacity-100 !ml-1 flex items-center ${
                sidebarPinned ? 'flex' : 'hidden group-hover:flex'
              }`}
            >
              <img src={logoWordmark} alt="" className="h-3.5 w-auto object-contain" aria-hidden />
            </span>
          </Link>
        </div>

        <nav className="flex-1 flex flex-col gap-1 px-2">
          {navItems.map((item) => (
            <Link key={item.key} to={inInstancePath(item.path)} className={navItemClass(isNavItemActive(item.key))}>
              <div className="w-10 h-10 flex items-center justify-center shrink-0">{navItemIcon(item.key)}</div>
              <span className="sidebar-label">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex flex-col gap-2 mt-auto px-2 shrink-0 pb-1 pt-2">
          <div className="flex items-center min-w-0 gap-0.5">
            <Link
              to={settingsHomePath}
              className={`${navItemClass(effectiveActive === 'settings')} flex-1 min-w-0`}
            >
              <div className="w-10 h-10 flex items-center justify-center shrink-0"><Settings className="w-5 h-5" /></div>
              <span className="sidebar-label">Einstellungen</span>
            </Link>
            <button
              type="button"
              className={pinButtonClass}
              onClick={toggleSidebarPinned}
              disabled={!sidebarPrefLoaded}
              title={sidebarPinned ? 'Sidebar bei Hover öffnen' : 'Sidebar fixieren'}
              aria-label={sidebarPinned ? 'Sidebar bei Hover öffnen' : 'Sidebar fixieren'}
              aria-pressed={sidebarPinned}
            >
              <Pin className={`w-4 h-4 ${sidebarPinned ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Mobile top bar (hidden on md+) ── */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#1b2531] flex items-center px-4 z-30 shrink-0">
        <Link to={inInstancePath('/dashboard')} className="flex items-center gap-2.5" aria-label="Zur Übersicht">
          <BrandIcon size={24} onDark />
          <img src={logoWordmark} alt="" className="h-3.5 w-auto object-contain" aria-hidden />
        </Link>
      </div>

      {/* ── Mobile bottom tab bar (hidden on md+) ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-[#1b2531] z-30 flex items-stretch border-t border-white/10">
        {canView('web.projekte.view') && (
        <Link
          to={inInstancePath('/projects')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${effectiveActive === 'leads' ? 'text-white' : 'text-slate-400 active:text-slate-200'}`}
          aria-label="Projekte"
        >
          <TrendingUp className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">Projekte</span>
        </Link>
        )}
        {canView('web.posteingang.view') && (
        <Link
          to={inInstancePath('/inbox')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${effectiveActive === 'inbox' ? 'text-white' : 'text-slate-400 active:text-slate-200'}`}
          aria-label="Posteingang"
        >
          <Mail className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">Postfach</span>
        </Link>
        )}
        {/* Übersicht — center */}
        <Link
          to={inInstancePath('/dashboard')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${effectiveActive === 'dashboard' ? 'text-white' : 'text-slate-400 active:text-slate-200'}`}
          aria-label="Übersicht"
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">Übersicht</span>
        </Link>
        {canView('web.kontakte.view') && (
        <Link
          to={inInstancePath('/contacts')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${effectiveActive === 'contacts' ? 'text-white' : 'text-slate-400 active:text-slate-200'}`}
          aria-label="Kontakte"
        >
          <Users className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">Kontakte</span>
        </Link>
        )}
        {/* Mehr */}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${drawerOpen ? 'text-white' : 'text-slate-400 active:text-slate-200'}`}
          aria-label="Mehr"
        >
          <MoreHorizontal className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">Mehr</span>
        </button>
      </div>

      {/* ── Mobile "Mehr" drawer (portal) ── */}
      {drawerOpen && createPortal(
        <div className="md:hidden fixed inset-0 z-[200] flex">
          {/* Drawer panel — full screen */}
          <nav
            className="relative w-full h-full bg-[#1b2531] flex flex-col overflow-y-auto"
            aria-label="Mehr Navigation"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 h-14 shrink-0 border-b border-white/10">
              <span className="text-white font-semibold text-base">Mehr</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:bg-white/10 transition-colors"
                aria-label="Menü schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav items */}
            <div className="flex-1 px-3 py-4 flex flex-col gap-0.5">
              {navItems
                .filter((item) => item.inMobileDrawer)
                .map((item) => (
                  <React.Fragment key={item.key}>
                    {drawerNavItem(inInstancePath(item.path), navItemIcon(item.key), item.label, isNavItemActive(item.key))}
                  </React.Fragment>
                ))}
            </div>

            {/* Drawer footer */}
            <div className="shrink-0 px-3 pb-20 pt-3 border-t border-white/10 flex flex-col gap-0.5">
              {drawerNavItem(settingsHomePath, <Settings className="w-5 h-5" />, 'Einstellungen', effectiveActive === 'settings')}
              <button
                type="button"
                onClick={() => { void handleLogout(); }}
                className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-slate-300 hover:bg-white/10 active:bg-white/20 transition-colors w-full text-left"
              >
                <span className="w-6 flex items-center justify-center shrink-0"><LogOut className="w-5 h-5" /></span>
                <span className="text-base font-medium">Abmelden</span>
              </button>
              <a
                href={IMPRESSUM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 pt-3 text-sm text-slate-500 hover:text-slate-300"
              >
                Impressum
              </a>
            </div>
          </nav>
        </div>,
        document.body
      )}
    </>
  );
};

